// Per-slot overrides — layered on top of the fill-mode builder output so a
// tester can tweak individual slots without writing a FROM_CONFIG JSON.
//
// An override is:
//   { empty?: boolean,
//     count?: number,
//     vendItemName?: string,
//     daysUntilExpiration?: number,
//     status?: 'AVAILABLE' | 'EXPIRED' }
//
// applyOverrides() runs after buildItemsBySlot() so the fill mode still
// produces the baseline. Only slots present in the override map are rewritten.

import { MS_PER_DAY, uuidv4, cstNow, addDays } from './seedUtils';

// Mirror fillModeBuilders.js: shift the nominal "expires in N days" anchor
// back by 12h so the target expiration lands mid-day.
const EXPIRATION_HOURS_OFFSET_MS = 12 * 60 * 60 * 1000;

function buildSlotFromOverride(slot, baseline, override, shelfLife) {
  if (override.empty) return { itemsInSlot: [] };

  const existingItem = (baseline.itemsInSlot || [])[0];
  const vendItemName =
    override.vendItemName ||
    (existingItem && existingItem.vendItemName) ||
    slot.vendItemName;

  if (!vendItemName || vendItemName === 'EMPTY_SLOT_NEW') {
    return { itemsInSlot: [] };
  }

  const existingCount = (baseline.itemsInSlot || []).length;
  const count =
    override.count != null
      ? Math.max(0, Number(override.count))
      : existingCount > 0
      ? existingCount
      : Number(slot.maxPerSlot || 0);

  const days =
    override.daysUntilExpiration != null
      ? Number(override.daysUntilExpiration)
      : undefined;
  const shelfLifeDays = shelfLife[vendItemName] ?? 7;
  const expirationOffset = days != null ? days : shelfLifeDays;

  const anchor = cstNow();
  // Same math as fillModeBuilders.targetExpiration / productionFor: shift the
  // target back 12h, then backdate production by the SKU's shelf life so a
  // 7-day SKU "expiring in 12h" reads as produced 6.5 days ago.
  const expiration = new Date(
    anchor.getTime() +
      expirationOffset * MS_PER_DAY -
      EXPIRATION_HOURS_OFFSET_MS,
  );
  const production = addDays(expiration, -shelfLifeDays);
  const status =
    override.status ||
    (expiration.getTime() < anchor.getTime() ? 'EXPIRED' : 'AVAILABLE');

  return {
    itemsInSlot: Array.from({ length: count }, () => ({
      uniqueId: uuidv4(),
      vendItemName,
      productionDate: production.getTime(),
      expirationDate: expiration.getTime(),
      status,
    })),
  };
}

export function applyOverrides(itemsBySlot, overrides, slots, shelfLife) {
  if (!overrides || Object.keys(overrides).length === 0) return itemsBySlot;
  const slotsByFmt = {};
  slots.forEach((s) => {
    slotsByFmt[`(${s.x},${s.y})`] = s;
  });
  const out = { ...itemsBySlot };
  Object.keys(overrides).forEach((fmt) => {
    const slot = slotsByFmt[fmt];
    if (!slot) return; // slot no longer exists in current layout — skip silently
    out[fmt] = buildSlotFromOverride(
      slot,
      itemsBySlot[fmt] || { itemsInSlot: [] },
      overrides[fmt],
      shelfLife,
    );
  });
  return out;
}

// Derive a plausible default for the modal's "days until expiration" field so
// the user sees what value the slot is currently using.
export function defaultDaysForSlot(slotFmt, itemsBySlot, shelfLife, slot) {
  const first =
    itemsBySlot &&
    itemsBySlot[slotFmt] &&
    itemsBySlot[slotFmt].itemsInSlot &&
    itemsBySlot[slotFmt].itemsInSlot[0];
  if (first) {
    const diff = Math.round(
      (first.expirationDate - cstNow().getTime()) / MS_PER_DAY,
    );
    return diff;
  }
  if (slot && slot.vendItemName && shelfLife[slot.vendItemName]) {
    return shelfLife[slot.vendItemName];
  }
  return 7;
}
